import {
  PRINT_FINISH_SIZES,
  PRINT_MIN_PPI,
  STORE_PRINT_SIZES,
  type StorePrintSize,
} from './constants.js';

export interface PixelDimensions {
  readonly width?: number | null;
  readonly height?: number | null;
}

const PRINT_SIZE_BY_LABEL = new Map<string, StorePrintSize>(
  STORE_PRINT_SIZES.map((size) => [size.label, size])
);

function isPositiveFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function formatDimensionToken(value: string): string {
  const n = Number(value);
  return Number.isFinite(n) ? String(n) : value;
}

/**
 * Normalize display names like `30x45"`, `30 x 45`, or `16x20 Framed` to the
 * shared `WxH` token used by STORE_PRINT_SIZES.
 */
export function normalizePrintSizeLabel(value: string): string {
  const match = value.toLowerCase().match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)/);
  if (!match) return '';
  const width = match[1];
  const height = match[2];
  if (!width || !height) return '';
  return `${formatDimensionToken(width)}x${formatDimensionToken(height)}`;
}

export function findStorePrintSize(value: string): StorePrintSize | null {
  return PRINT_SIZE_BY_LABEL.get(normalizePrintSizeLabel(value)) ?? null;
}

export function hasPrintableDimensions(dimensions: PixelDimensions | null | undefined): dimensions is {
  readonly width: number;
  readonly height: number;
} {
  return (
    dimensions != null &&
    isPositiveFiniteNumber(dimensions.width) &&
    isPositiveFiniteNumber(dimensions.height)
  );
}

/**
 * Long-edge pixels left after fitting the source to the print aspect. This is
 * the resolution that reaches paper; using the uncropped long edge overstates
 * PPI for non-matching aspects.
 */
export function fittedPrintLongEdgePixels(
  dimensions: PixelDimensions | null | undefined,
  size: StorePrintSize
): number | null {
  if (!hasPrintableDimensions(dimensions)) return null;

  const sourceLongPx = Math.max(dimensions.width, dimensions.height);
  const sourceShortPx = Math.min(dimensions.width, dimensions.height);
  const printLongIn = Math.max(size.widthIn, size.heightIn);
  const printShortIn = Math.min(size.widthIn, size.heightIn);
  const targetAspect = printLongIn / printShortIn;
  const sourceAspect = sourceLongPx / sourceShortPx;

  return sourceAspect >= targetAspect
    ? Math.round(sourceShortPx * targetAspect)
    : sourceLongPx;
}

export function printSizePpi(
  dimensions: PixelDimensions | null | undefined,
  size: StorePrintSize
): number | null {
  const fittedLongPx = fittedPrintLongEdgePixels(dimensions, size);
  if (fittedLongPx === null) return null;
  return fittedLongPx / Math.max(size.widthIn, size.heightIn);
}

/**
 * Sellability check for store/API gates. Fail-closed on missing dimensions,
 * malformed dimensions, and unknown product-option size labels. Dimensions
 * must be the MEASURED print source (photos.width/height carries source dims
 * since the 2026-08-15 backfill, outstanding-pvz5); a 1024px web-derivative
 * row fails every size, which is the correct answer — the fail-open carve-out
 * that once covered such rows (outstanding-92g5) is gone.
 */
export function isPrintSizeSellableForDimensions(
  dimensions: PixelDimensions | null | undefined,
  sizeName: string,
  minPpi = PRINT_MIN_PPI
): boolean {
  const size = findStorePrintSize(sizeName);
  if (!size || !Number.isFinite(minPpi) || minPpi <= 0) return false;
  const ppi = printSizePpi(dimensions, size);
  return ppi !== null && ppi >= minPpi;
}

/**
 * Whether a finish (`print_type` option name, any case) is cut in a given
 * print size (option display name, normalized). Fail-closed: an unknown
 * finish, or a size the finish's PRINT_FINISH_SIZES row does not list, is
 * not sellable. The API (validateLineItems) and the detail page's finish
 * dropdown both read this, so the storefront can only offer what checkout
 * will accept.
 */
export function isPrintFinishSellableForSize(finishName: string, sizeName: string): boolean {
  const finish = finishName.trim().toLowerCase();
  if (!Object.prototype.hasOwnProperty.call(PRINT_FINISH_SIZES, finish)) return false;
  const sizes: readonly string[] = PRINT_FINISH_SIZES[finish as keyof typeof PRINT_FINISH_SIZES];
  const size = normalizePrintSizeLabel(sizeName);
  return size !== '' && sizes.includes(size);
}
